import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getAuth } from 'firebase/auth';
import { WherebyProvider, VideoView, useRoomConnection } from '@whereby.com/browser-sdk/react';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../config/firebase';
import { postSessionPipelineService, PostSessionResult } from '../services/PostSessionPipelineService';

interface VideoSessionWherebyProps {
  appointmentId: string;     // Appointment id, or "meeting-<id>" for staff meetings
  patientId: string;
  patientName: string;
  patient2Id?: string;       // Second client for couples sessions
  patient2Name?: string;
  providerId: string;
  providerName: string;
  role: 'provider' | 'client';
  currentUserId?: string;    // Auth UID (or linked patient id) of current user — picks the couples display name
  onEndSession: (pipelineResult?: PostSessionResult) => void;
}

/** What /api/video-room returns. roomKey is present for hosts only. */
interface RoomAccess {
  roomUrl: string;
  roomKey?: string;
  isHost: boolean;
}

type RoomConnection = ReturnType<typeof useRoomConnection>;

/** Statuses where a patient is outside a locked room, waiting to be let in. */
const KNOCK_STATUSES = ['room_locked', 'knocking', 'knock_on_hold'];

const VideoSessionWhereby: React.FC<VideoSessionWherebyProps> = (props) => {
  return (
    <WherebyProvider>
      <VideoCall {...props} />
    </WherebyProvider>
  );
};

// Fetches this user's access to the session's room from the server, then
// hands off to ConnectedCall. The Whereby API key never reaches the browser,
// and the host roomKey only reaches the provider.
const VideoCall: React.FC<VideoSessionWherebyProps> = (props) => {
  const { appointmentId, role, patientName, patient2Id, patient2Name, providerName, currentUserId } = props;
  const [access, setAccess] = useState<RoomAccess | null>(null);
  const [roomError, setRoomError] = useState<string | null>(null);
  const [startedAt] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    const fetchRoom = async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) throw new Error('You are signed out. Please sign in again.');
        const response = await fetch('/api/video-room', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ appointmentId }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `Video server error: ${response.status}`);
        if (!cancelled) setAccess(data as RoomAccess);
      } catch (err: any) {
        console.error('[VideoSession] Room fetch failed:', err);
        if (!cancelled) setRoomError(err?.message || 'Could not reach the video server');
      }
    };
    fetchRoom();
    return () => { cancelled = true; };
  }, [appointmentId]);

  // Name shown to the other participants. Couples: the second client is the
  // one whose id matches patient2Id.
  const displayName = role === 'provider' ? providerName
    : (patient2Id && currentUserId === patient2Id) ? (patient2Name || 'Partner')
    : patientName;

  return access ? (
    <ConnectedCall {...props} access={access} displayName={displayName} startedAt={startedAt} />
  ) : (
    <CallScreen {...props} conn={null} isHost={false} roomError={roomError} startedAt={startedAt} />
  );
};

interface ConnectedCallProps extends VideoSessionWherebyProps {
  access: RoomAccess;
  displayName: string;
  startedAt: number;
}

// Owns the Whereby connection. Mounted only once the room is known, because
// the SDK's joinRoom reads the room URL and key from the first render.
const ConnectedCall: React.FC<ConnectedCallProps> = ({ access, displayName, ...rest }) => {
  const options = useMemo(() => ({
    localMediaOptions: { audio: true, video: true },
    displayName,
    roomKey: access.roomKey,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);
  const conn = useRoomConnection(access.roomUrl, options);
  const { joinRoom, knock } = conn.actions;
  const status = conn.state.connectionStatus;

  // Join once. The hook itself leaves the room on unmount.
  useEffect(() => {
    joinRoom().catch((err) => console.warn('[VideoSession] joinRoom:', err));
  }, [joinRoom]);

  // Rooms are locked. Without the host key, joining lands on room_locked —
  // knock once so the provider sees this person in their waiting list.
  const knockedRef = useRef(false);
  useEffect(() => {
    if (status === 'room_locked' && !access.isHost && !knockedRef.current) {
      knockedRef.current = true;
      knock();
    }
  }, [status, access.isHost, knock]);

  return <CallScreen {...rest} conn={conn} isHost={access.isHost} roomError={null} />;
};

interface CallScreenProps extends VideoSessionWherebyProps {
  conn: RoomConnection | null;
  isHost: boolean;
  roomError: string | null;
  startedAt: number;
}

const CallScreen: React.FC<CallScreenProps> = ({
  conn,
  isHost,
  roomError,
  startedAt,
  appointmentId,
  patientId,
  patientName,
  providerId,
  providerName,
  role,
  onEndSession
}) => {
  const status = conn?.state.connectionStatus;
  const isJoined = status === 'connected';
  const remoteParticipants = conn?.state.remoteParticipants ?? [];
  const waitingParticipants = isHost ? (conn?.state.waitingParticipants ?? []) : [];
  const localStream = conn?.state.localParticipant?.stream;

  // State for controls
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [sessionDuration, setSessionDuration] = useState(() => Math.floor((Date.now() - startedAt) / 1000));
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [slowToConnect, setSlowToConnect] = useState(false);
  // Remote mute: which remote participants the provider has muted (by participant id).
  // Local playback only — it does not mute them for anyone else.
  const [mutedRemoteUsers, setMutedRemoteUsers] = useState<Set<string>>(new Set());

  // Recording refs — single recorder with pause/resume support
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const finalAudioBlobRef = useRef<Blob | null>(null);  // Saved when recording stops
  const audioStreamRef = useRef<MediaStream | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  // Track whether a recording was made during this session
  const hasRecordingRef = useRef(false);

  // Toggle mute for a remote participant
  const toggleRemoteMute = (id: string) => {
    setMutedRemoteUsers(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Session timer
  useEffect(() => {
    const timer = setInterval(() => {
      setSessionDuration(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startedAt]);

  // The SDK doesn't surface a denied camera/microphone — the join just never
  // completes. Offer a hint if connecting drags on.
  useEffect(() => {
    if (isJoined) {
      setSlowToConnect(false);
      return;
    }
    const timer = setTimeout(() => setSlowToConnect(true), 20000);
    return () => clearTimeout(timer);
  }, [isJoined]);

  // Format session duration
  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleMic = () => {
    const newState = !isMicOn;
    conn?.actions.toggleMicrophone(newState);
    setIsMicOn(newState);
  };

  const toggleCamera = () => {
    const newState = !isCameraOn;
    conn?.actions.toggleCamera(newState);
    setIsCameraOn(newState);
  };

  // Start audio recording (provider only) — creates a single recorder for the session
  const startRecording = useCallback(async () => {
    if (role !== 'provider') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];

      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus' : 'audio/webm'
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      recorder.start(5000); // Collect chunks every 5 seconds
      mediaRecorderRef.current = recorder;
      hasRecordingRef.current = true;
      setIsRecording(true);
      setIsPaused(false);
    } catch (error) {
      console.error('Failed to start recording:', error);
    }
  }, [role]);

  // Pause recording — keeps the stream alive, just stops capturing
  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.pause();
      setIsPaused(true);
    }
  }, []);

  // Resume recording after pause
  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'paused') {
      recorder.resume();
      setIsPaused(false);
    }
  }, []);

  // Stop recording completely and return audio blob
  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        if (audioChunksRef.current.length > 0) {
          resolve(new Blob(audioChunksRef.current, { type: 'audio/webm' }));
        } else {
          resolve(null);
        }
        return;
      }

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        finalAudioBlobRef.current = audioBlob;  // Save for later if session ends after stop
        audioStreamRef.current?.getTracks().forEach(t => t.stop());
        setIsRecording(false);
        setIsPaused(false);
        resolve(audioBlob);
      };

      recorder.stop();
    });
  }, []);

  // Provider stops recording permanently (before ending session or done recording)
  const handleStopRecording = async () => {
    await stopRecording();
  };

  // Handle end session with post-session pipeline
  const handleEndSession = () => {
    setShowEndConfirm(true);
  };

  const confirmEndSession = async () => {
    setShowEndConfirm(false);

    // Stop recording if still active, or use previously saved blob
    let audioBlob: Blob | null = null;
    if (isRecording) {
      audioBlob = await stopRecording();
    } else if (hasRecordingRef.current && finalAudioBlobRef.current) {
      audioBlob = finalAudioBlobRef.current;
    }

    // Leave the Whereby room
    conn?.actions.leaveRoom();

    // For providers: save audio + run the post-session pipeline only if recording was made
    if (role === 'provider' && hasRecordingRef.current && audioBlob && audioBlob.size > 1000) {
      setIsProcessing(true);
      setProcessingStatus('Saving session audio...');

      try {
        const sessionId = `session-${appointmentId}-${Date.now()}`;

        // Upload audio to Firebase Storage for HIPAA record retention
        try {
          const audioRef = ref(storage, `session_recordings/${providerId}/${sessionId}.webm`);
          await uploadBytes(audioRef, audioBlob, { contentType: 'audio/webm' });
        } catch (uploadErr) {
          console.error('[VideoSession] Audio upload failed (continuing pipeline):', uploadErr);
        }

        setProcessingStatus('Transcribing with speaker diarization...');
        const result = await postSessionPipelineService.runPipeline(
          audioBlob,
          sessionId,
          appointmentId,
          providerId,
          patientId,
          'SOAP' // Default template
        );

        setProcessingStatus('Complete! AI notes ready for review.');
        setTimeout(() => onEndSession(result), 1500);
      } catch (error) {
        console.error('Post-session pipeline error:', error);
        setProcessingStatus('Processing complete (some steps may have failed).');
        setTimeout(() => onEndSession(), 2000);
      }
    } else {
      onEndSession();
    }
  };

  // What to show in the main area while no one else is on screen.
  const renderWaiting = () => {
    if (roomError) {
      return (
        <>
          <p style={{ ...styles.waitingText, color: '#EF4444' }}>Connection Error</p>
          <p style={styles.waitingSubtext}>{roomError}</p>
          {role === 'provider' && (
            <p style={{ ...styles.waitingSubtext, marginTop: '8px' }}>
              Ensure WHEREBY_API_KEY is set in Vercel environment variables.
            </p>
          )}
        </>
      );
    }
    if (status === 'knock_rejected') {
      return (
        <>
          <p style={{ ...styles.waitingText, color: '#EF4444' }}>You weren't admitted to this session</p>
          <p style={styles.waitingSubtext}>Close this window and try again from your dashboard, or message {providerName}.</p>
        </>
      );
    }
    if (status === 'kicked') {
      return <p style={styles.waitingText}>You were removed from the session.</p>;
    }

    let message = 'Connecting to video server...';
    let subtext: string | null = null;
    if (status && KNOCK_STATUSES.includes(status)) {
      message = `Waiting for ${providerName} to let you in...`;
      subtext = "They'll see that you're here.";
    } else if (status === 'reconnecting' || status === 'disconnected') {
      message = 'Reconnecting...';
    } else if (isJoined) {
      message = role === 'provider'
        ? (patientId ? 'Waiting for patient to join...' : 'Waiting for others to join...')
        : 'Waiting for provider to join...';
    } else if (slowToConnect) {
      subtext = 'Taking longer than usual — make sure your browser allowed camera and microphone access.';
    }

    return (
      <>
        <div style={styles.waitingSpinner}></div>
        <p style={styles.waitingText}>{message}</p>
        {subtext && <p style={styles.waitingSubtext}>{subtext}</p>}
      </>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          {isRecording && !isPaused ? (
            <div style={styles.recordingIndicator}>
              <span style={styles.recordingDot}></span>
              <span style={styles.recordingText}>REC</span>
            </div>
          ) : isRecording && isPaused ? (
            <div style={styles.recordingIndicator}>
              <span style={{ ...styles.recordingDot, backgroundColor: '#D97706', animation: 'none' }}></span>
              <span style={{ ...styles.recordingText, color: '#D97706' }}>PAUSED</span>
            </div>
          ) : (
            <div style={styles.recordingIndicator}>
              <span style={{ ...styles.recordingDot, backgroundColor: '#6b7280', animation: 'none' }}></span>
              <span style={{ ...styles.recordingText, color: '#9ca3af' }}>LIVE SESSION</span>
            </div>
          )}
          <span style={styles.duration}>{formatDuration(sessionDuration)}</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.participantName}>
            {role === 'provider' ? `Patient: ${patientName}` : `Provider: ${providerName}`}
          </span>
        </div>
      </div>

      {/* Video Grid */}
      <div style={styles.videoGrid}>
        {/* Waiting room — hosts admit people who knocked */}
        {waitingParticipants.length > 0 && (
          <div style={styles.lobbyBanner}>
            {waitingParticipants.map((waiting) => (
              <div key={waiting.id} style={styles.lobbyRow}>
                <span style={styles.lobbyText}>
                  {waiting.displayName || 'Someone'} is waiting to join
                </span>
                <button
                  onClick={() => conn?.actions.acceptWaitingParticipant(waiting.id)}
                  style={styles.lobbyAdmitBtn}
                >
                  Admit
                </button>
                <button
                  onClick={() => conn?.actions.rejectWaitingParticipant(waiting.id)}
                  style={styles.lobbyDeclineBtn}
                >
                  Decline
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Remote Video (Main) */}
        <div style={styles.remoteVideoContainer}>
          {remoteParticipants.length > 0 ? (
            remoteParticipants.map((participant) => {
              const remoteName = participant.displayName || 'Participant';
              const isMuted = mutedRemoteUsers.has(participant.id);
              return (
                <div key={participant.id} style={styles.remoteVideo}>
                  {participant.stream && (
                    <VideoView stream={participant.stream} muted={isMuted} style={styles.remoteVideoFill} />
                  )}
                  {(!participant.stream || !participant.isVideoEnabled) && (
                    <div style={{ ...styles.cameraOffPlaceholder, position: 'absolute', inset: 0 }}>
                      <span style={styles.cameraOffIcon}>📹</span>
                      <span style={styles.cameraOffText}>Camera Off</span>
                    </div>
                  )}
                  <div style={styles.videoLabel}>
                    {remoteName}
                    {isMuted && <span style={{ color: '#EF4444', marginLeft: '6px', fontSize: '11px' }}>(muted)</span>}
                  </div>
                  {/* Provider can mute/unmute remote participants */}
                  {role === 'provider' && (
                    <button
                      onClick={() => toggleRemoteMute(participant.id)}
                      title={isMuted ? `Unmute ${remoteName}` : `Mute ${remoteName}`}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isMuted ? 'rgba(239, 68, 68, 0.85)' : 'rgba(0, 0, 0, 0.5)',
                        color: '#fff',
                        zIndex: 10,
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {isMuted ? '🔇' : '🎤'}
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div style={styles.waitingContainer}>
              {renderWaiting()}
            </div>
          )}
        </div>

        {/* Local Video (Picture-in-Picture) */}
        <div style={styles.localVideoContainer}>
          {isCameraOn && localStream ? (
            <>
              <VideoView stream={localStream} muted mirror style={styles.localVideo} />
              <div style={styles.videoLabel}>You ({role})</div>
            </>
          ) : (
            <div style={styles.cameraOffPlaceholder}>
              <span style={styles.cameraOffIcon}>📹</span>
              <span style={styles.cameraOffText}>Camera Off</span>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <button
          onClick={toggleMic}
          style={{
            ...styles.controlButton,
            ...(isMicOn ? styles.controlButtonActive : styles.controlButtonMuted)
          }}
          title={isMicOn ? 'Mute Microphone' : 'Unmute Microphone'}
        >
          {isMicOn ? '🎤' : '🔇'} {isMicOn ? 'Mute' : 'Unmute'}
        </button>

        <button
          onClick={toggleCamera}
          style={{
            ...styles.controlButton,
            ...(isCameraOn ? styles.controlButtonActive : styles.controlButtonMuted)
          }}
          title={isCameraOn ? 'Turn Camera Off' : 'Turn Camera On'}
        >
          {isCameraOn ? '📹' : '📴'} {isCameraOn ? 'Camera On' : 'Camera Off'}
        </button>

        {/* Recording controls — provider only */}
        {role === 'provider' && !isRecording && !hasRecordingRef.current && (
          <button
            onClick={startRecording}
            style={{
              ...styles.controlButton,
              backgroundColor: '#059669',
              color: '#fff',
              minWidth: '140px',
            }}
            title="Start Recording"
          >
            ⏺ Start Recording
          </button>
        )}

        {role === 'provider' && isRecording && !isPaused && (
          <button
            onClick={pauseRecording}
            style={{
              ...styles.controlButton,
              backgroundColor: '#D97706',
              color: '#fff',
              minWidth: '120px',
            }}
            title="Pause Recording"
          >
            ⏸ Pause
          </button>
        )}

        {role === 'provider' && isRecording && isPaused && (
          <button
            onClick={resumeRecording}
            style={{
              ...styles.controlButton,
              backgroundColor: '#059669',
              color: '#fff',
              minWidth: '120px',
            }}
            title="Resume Recording"
          >
            ▶ Resume
          </button>
        )}

        {role === 'provider' && isRecording && (
          <button
            onClick={handleStopRecording}
            style={{
              ...styles.controlButton,
              backgroundColor: '#DC2626',
              color: '#fff',
              minWidth: '120px',
            }}
            title="Stop Recording"
          >
            ⏹ Stop
          </button>
        )}

        <button
          onClick={handleEndSession}
          style={styles.endButton}
          title="End Session"
        >
          📞 End Session
        </button>
      </div>

      {/* Connection Status */}
      <div style={styles.statusBar}>
        <div style={styles.statusIndicator}>
          <span style={styles.statusDot}></span>
          <span style={styles.statusText}>
            {isJoined ? 'Connected'
              : status && KNOCK_STATUSES.includes(status) ? 'Waiting to be admitted'
              : 'Connecting...'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={styles.statusInfo}>
            Participants: {remoteParticipants.length + 1} | Quality: HD
          </div>
          {isRecording && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: isPaused ? 'rgba(217, 119, 6, 0.15)' : 'rgba(220, 38, 38, 0.15)',
              border: `1px solid ${isPaused ? 'rgba(217, 119, 6, 0.4)' : 'rgba(220, 38, 38, 0.4)'}`,
              borderRadius: '6px',
              padding: '4px 10px',
            }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isPaused ? '#D97706' : '#EF4444', animation: isPaused ? 'none' : 'pulse 1.5s infinite' }}></span>
              <span style={{ color: isPaused ? '#D97706' : '#EF4444', fontSize: '12px', fontWeight: '600' }}>
                {isPaused ? 'Recording Paused' : 'Session Recording Active'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Post-session processing overlay */}
      {isProcessing && (
        <div style={styles.processingOverlay}>
          <div style={styles.processingCard}>
            <div style={styles.processingSpinner}></div>
            <h3 style={{ color: '#fff', margin: '16px 0 8px' }}>Processing Session</h3>
            <p style={{ color: '#d1d5db', margin: 0 }}>{processingStatus}</p>
          </div>
        </div>
      )}

      {/* End Session Confirm Modal */}
      {showEndConfirm && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmCard}>
            <h3 style={{ color: '#fff', margin: '0 0 12px', fontSize: '18px' }}>End Session?</h3>
            <p style={{ color: '#d1d5db', margin: '0 0 24px', fontSize: '14px', lineHeight: '1.5' }}>
              {isRecording
                ? 'This will stop the recording and generate the AI clinical note and session transcript.'
                : hasRecordingRef.current
                  ? 'The recording has been saved. AI clinical notes will be generated from the recorded portion.'
                  : 'No recording was made during this session. The call will end without generating AI notes.'
              }
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEndConfirm(false)}
                style={styles.confirmCancelBtn}
              >
                Cancel
              </button>
              <button
                onClick={confirmEndSession}
                style={styles.confirmEndBtn}
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#1a1a1a',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9999,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
    backgroundColor: '#2d2d2d',
    borderBottom: '1px solid #444',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
  },
  recordingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  recordingDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#EF4444',
    animation: 'pulse 2s infinite',
  },
  recordingText: {
    color: '#EF4444',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  duration: {
    color: '#fff',
    fontSize: '18px',
    fontFamily: 'monospace',
    fontWeight: 'bold',
  },
  participantName: {
    color: '#6CA6C1',
    fontSize: '16px',
    fontWeight: '500',
  },
  videoGrid: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  remoteVideoContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#000',
    borderRadius: '12px',
    overflow: 'hidden',
    position: 'relative',
  },
  remoteVideo: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  remoteVideoFill: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  localVideoContainer: {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    width: '160px',
    height: '120px',
    backgroundColor: '#2d2d2d',
    borderRadius: '10px',
    overflow: 'hidden',
    border: '2px solid #6CA6C1',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
    cursor: 'grab',
    zIndex: 10,
  },
  localVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  videoLabel: {
    position: 'absolute',
    bottom: '10px',
    left: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
  },
  lobbyBanner: {
    position: 'absolute',
    top: '28px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    zIndex: 20,
    maxWidth: 'calc(100% - 40px)',
  },
  lobbyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap' as const,
    backgroundColor: 'rgba(45, 45, 45, 0.95)',
    border: '1px solid #6CA6C1',
    borderRadius: '10px',
    padding: '10px 14px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
  },
  lobbyText: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
  },
  lobbyAdmitBtn: {
    padding: '6px 14px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#059669',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
  },
  lobbyDeclineBtn: {
    padding: '6px 14px',
    borderRadius: '6px',
    border: '1px solid #555',
    backgroundColor: 'transparent',
    color: '#d1d5db',
    cursor: 'pointer',
    fontSize: '13px',
  },
  waitingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
    padding: '0 20px',
    textAlign: 'center' as const,
  },
  waitingSpinner: {
    width: '60px',
    height: '60px',
    border: '6px solid #444',
    borderTop: '6px solid #6CA6C1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  waitingText: {
    color: '#fff',
    fontSize: '20px',
    margin: 0,
  },
  waitingSubtext: {
    color: '#999',
    fontSize: '14px',
    margin: 0,
  },
  cameraOffPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a1a1a',
    gap: '10px',
  },
  cameraOffIcon: {
    fontSize: '48px',
    opacity: 0.5,
  },
  cameraOffText: {
    color: '#999',
    fontSize: '14px',
  },
  controls: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 10px',
    backgroundColor: '#2d2d2d',
    borderTop: '1px solid #444',
    flexWrap: 'wrap' as const,
  },
  controlButton: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '100px',
  },
  controlButtonActive: {
    backgroundColor: '#6CA6C1',
    color: '#fff',
  },
  controlButtonMuted: {
    backgroundColor: '#444',
    color: '#999',
  },
  endButton: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#EF4444',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '100px',
  },
  statusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 30px',
    backgroundColor: '#1a1a1a',
    borderTop: '1px solid #444',
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#10B981',
  },
  statusText: {
    color: '#10B981',
    fontSize: '14px',
    fontWeight: '500',
  },
  statusInfo: {
    color: '#999',
    fontSize: '14px',
  },
  processingOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  processingCard: {
    textAlign: 'center' as const,
    padding: '40px',
  },
  processingSpinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #374151',
    borderTop: '4px solid #D4A017',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto',
  },
  confirmOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
  },
  confirmCard: {
    backgroundColor: '#2d2d2d',
    border: '1px solid #444',
    borderRadius: '12px',
    padding: '32px',
    width: '380px',
    maxWidth: '90vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  confirmCancelBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '1px solid #555',
    backgroundColor: 'transparent',
    color: '#d1d5db',
    cursor: 'pointer',
    fontSize: '14px',
  },
  confirmEndBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#EF4444',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
  },
};

export default VideoSessionWhereby;
